import { Question, QuestionType, StudentGender, StudentGenderToLabel, AudioType, Bucket } from '@mightybyte/business';
import { Label } from '../ui/label';

interface QuestionDetailsViewProps {
  question: Question;
}

const audioTypeLabels: Record<AudioType, string> = {
  [AudioType.Correct]: 'Correct Answer',
  [AudioType.Incorrect]: 'Incorrect Answer',
  [AudioType.Underestimated]: 'Underestimated Answer',
  [AudioType.Overestimated]: 'Overestimated Answer',
};

const bucketLabels = {
  [Bucket.One]: 'Bucket 1',
  [Bucket.Two]: 'Bucket 2',
};

export function QuestionDetailsView({ question }: QuestionDetailsViewProps) {
  const renderAnswerSettings = () => {
    switch (question.answerProps.type) {
      case QuestionType.Boolean:
        return (
          <div className="space-y-2 p-4 bg-primary-foreground rounded-lg">
            <Label className="text-sm font-semibold text-foreground/60">Display Format</Label>
            <p className="text-sm text-foreground/80">
              {question.answerProps.props.displayFormat === 'yesNo' ? 'Yes/No' : 'True/False'}
            </p>
          </div>
        );

      case QuestionType.Numeric:
        return (
          <div className="space-y-2 p-4 bg-primary-foreground rounded-lg">
            <Label className="text-sm font-semibold text-foreground/60">Answer Range {question.answerProps.props.units ? `(${question.answerProps.props.units})` : ''}</Label>
            <p className="text-sm text-foreground/80">
              {question.answerProps.props.min} - {question.answerProps.props.max}
              {question.answerProps.props.useStrictMax && ' (strict max)'}
            </p>
          </div>
        );

      case QuestionType.Percentage:
        return (
          <div className="space-y-2 p-4 bg-primary-foreground rounded-lg">
            <Label className="text-sm font-semibold text-foreground/60">Answer Range</Label>
            <p className="text-sm text-foreground/80">0 - 100%</p>
          </div>
        );

      case QuestionType.SingleChoice:
        return (
          <div className="space-y-2 p-4 bg-primary-foreground rounded-lg">
            <Label className="text-sm font-semibold text-foreground/60">Options</Label>
            <ul className="list-disc list-inside text-sm text-foreground/80 space-y-1">
              {question.answerProps.props.options.map((option, idx) => (
                <li key={idx}>{option}</li>
              ))}
            </ul>
          </div>
        );

      default:
        return null;
    }
  };

  const renderActualValues = () => {
    if (!question.actualValues) return null;

    // For Single Choice questions, display is different (percentage breakdown per option)
    if (question.actualValues.type === QuestionType.SingleChoice) {
      const genders = [StudentGender.son, StudentGender.daughter, StudentGender.preferNotToAnswer];

      return (
        <div className="space-y-2 p-4 bg-primary-foreground rounded-lg">
          <Label className="text-sm font-semibold text-foreground/60">Actual Answer Distribution</Label>
          <div className="space-y-3">
            {genders.map((gender) => {
              const optionPercentages = question.actualValues!.values[gender];
              if (!optionPercentages || typeof optionPercentages !== 'object') return null;

              return (
                <div key={gender} className="space-y-1">
                  <div className="font-semibold text-sm">{StudentGenderToLabel[gender]}:</div>
                  <div className="pl-3 space-y-0.5 text-sm text-foreground/80">
                    {Object.entries(optionPercentages as Record<string, number>).map(([option, percentage]) => (
                      <div key={option}>
                        {option}: <span className="font-medium">{percentage}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    // For other question types (Boolean, Numeric, Percentage)
    const genders = [StudentGender.son, StudentGender.daughter, StudentGender.preferNotToAnswer];

    // Get units from answer props if it's a numeric question
    const units = question.answerProps.type === QuestionType.Numeric && question.answerProps.props.units
      ? question.answerProps.props.units
      : '';

    return (
      <div className="space-y-2 p-4 bg-primary-foreground rounded-lg">
        <Label className="text-sm font-semibold text-foreground/60">Actual Answer</Label>
        <div className="flex flex-wrap gap-4 text-sm text-foreground/80">
          {genders.map((gender) => {
            const value = question.actualValues!.values[gender];
            if (value === undefined) return null;

            let displayValue: string;
            if (question.actualValues!.type === QuestionType.Boolean) {
              displayValue = value ? 'Yes/True' : 'No/False';
            } else if (question.actualValues!.type === QuestionType.Percentage) {
              displayValue = `${value}%`;
            } else if (question.actualValues!.type === QuestionType.Numeric) {
              displayValue = units ? `${value} ${units}` : String(value);
            } else {
              displayValue = String(value);
            }

            return (
              <span key={gender}>
                <span className="font-semibold">{StudentGenderToLabel[gender]}:</span> {displayValue}
              </span>
            );
          })}
        </div>
      </div>
    );
  };

  const renderAudios = () => {
    if (!question.audios || Object.keys(question.audios).length === 0) return null;

    const audioEntries = Object.entries(question.audios).filter(([_, audioInfo]) => audioInfo);

    if (audioEntries.length === 0) return null;

    return (
      <div className="space-y-3 p-4 bg-primary-foreground rounded-lg">
        <Label className="text-sm font-semibold text-foreground/60">Audios</Label>
        <div className="space-y-3">
          {audioEntries.map(([audioType, audioInfo]) => {
            if (!audioInfo) return null;

            const url = typeof audioInfo === 'object' && 'url' in audioInfo ? audioInfo.url : audioInfo;
            const fileName = typeof audioInfo === 'object' && 'fileName' in audioInfo ? audioInfo.fileName : undefined;

            const transcript = typeof audioInfo === 'object' && 'transcript' in audioInfo ? (audioInfo as any).transcript : undefined;

            return (
              <div key={audioType} className="space-y-1">
                <p className="text-sm text-foreground">{audioTypeLabels[audioType as AudioType]}</p>
                {fileName && (
                  <p className="text-xs text-foreground/50 font-mono">{fileName}</p>
                )}
                <audio
                  src={url}
                  controls
                  className="w-full h-9"
                  style={{ maxWidth: '100%' }}
                />
                {transcript && (
                  <div className="mt-1 p-2 bg-muted rounded text-xs text-foreground/70 whitespace-pre-wrap">
                    <span className="font-semibold text-foreground/50 block mb-0.5">Transcript</span>
                    {transcript}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderBucketConfiguration = () => {
    if (!question.isBucketQuestion || !question.resultsProps) return null;

    return (
      <div className="space-y-2 p-3 bg-primary/30 rounded-lg">
        <Label className="text-sm font-semibold">Bucket Question Configuration</Label>

        {question.resultsProps.type === QuestionType.Boolean && (
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-foreground/80">True → </span>
              <span className="font-medium">{bucketLabels[question.resultsProps.props.trueValue]}</span>
            </div>
            <div>
              <span className="text-foreground/80">False → </span>
              <span className="font-medium">{bucketLabels[question.resultsProps.props.falseValue]}</span>
            </div>
          </div>
        )}

        {(question.resultsProps.type === QuestionType.Numeric || question.resultsProps.type === QuestionType.Percentage) && (
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-foreground/80">Threshold: </span>
              <span className="font-medium">{question.resultsProps.props.threshold}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="text-foreground/80">≤ Threshold → </span>
                <span className="font-medium">{bucketLabels[question.resultsProps.props.belowThreshold]}</span>
              </div>
              <div>
                <span className="text-foreground/80">&gt; Threshold → </span>
                <span className="font-medium">{bucketLabels[question.resultsProps.props.aboveOrEqualThreshold]}</span>
              </div>
            </div>
          </div>
        )}

        {question.resultsProps.type === QuestionType.SingleChoice && question.answerProps.type === QuestionType.SingleChoice && (
          <div className="space-y-1 text-sm">
            {question.answerProps.props.options.map((option) => {
              const bucket = question.resultsProps?.type === QuestionType.SingleChoice
                ? question.resultsProps.props.optionToBucketMap[option]
                : undefined;
              return (
                <div key={option}>
                  <span className="text-foreground/80">{option} → </span>
                  <span className="font-medium">{bucket ? bucketLabels[bucket] : 'Not assigned'}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 pt-4 ">
      {/* Left Column - Details */}
      <div className="space-y-4">
        {/* Answer Settings */}
        {renderAnswerSettings()}

        {/* Actual Values */}
        {renderActualValues()}

        {/* Pair with Result Number */}
        {question.pairWithResultNumber && (
          <div className="space-y-2 p-4 bg-primary-foreground rounded-lg">
            <Label className="text-sm font-semibold text-foreground/60">Results Display</Label>
            <p className="text-sm text-foreground/80">
              Paired with Question {question.pairWithResultNumber}
            </p>
          </div>
        )}

          {/* Audios */}
          {renderAudios()}
        {/* Bucket Configuration */}
        {renderBucketConfiguration()}

      </div>

      {/* Right Column - Image */}
      {question.image && (
        <div className="flex justify-center lg:justify-end items-start">
          <img
            src={question.image.large || question.image.medium}
            alt={`Question ${question.number}`}
            className="rounded-lg border border-border w-full max-w-[400px] h-auto object-contain"
          />
        </div>
      )}
    </div>
  );
}
